import { inject, Injectable, Signal } from '@angular/core';
import { AppForm } from '../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import {
  TaxExemptionBasisAppEntityDictionaryTaxExemptionBasis,
  TaxExemptionBasisService,
  TaxObjectAppEntityTaxObjectWrite
} from '../../../../../../openapi';
import { toSignal } from '@angular/core/rxjs-interop';
import { toNumber } from '../../../../../../services/helpers';

export type AdditionalIdentificationType
  = 'Numer zbioru dokumentów'
  | 'Numer i data aktu notarialnego'
  | 'Numer i data umowy najmu/dzierżawy'
  | '';

@Injectable()
export class TaxObjectItemForm extends AppForm {

  public groundTypes: string[] = [
    'Grunty orne (R)',
    'Łąki (Ł)',
    'Pastwiska (Ps)',
    'Sady (S)',
    'Rolne zabudowane (Br)',
    'Stawy (Wsr)',
    'Rowy (W)',
    'Grunty zadrzewione i zakrzewione na użytkach rolnych (Lzr)'
  ];

  public usageClasses: string[] = [
    'Klasa I',
    'Klasa II',
    'Klasa IIIa',
    'Klasa IIIb',
    'Klasa IVa',
    'Klasa IVb',
    'Klasa V',
    'Klasa VI',
    'Klasa VIz'
  ];

  public propertyTypes: string[] = [
    'Dom jednorodzinny',
    'Mieszkanie/Lokal użytkowy',
    'Działka',
    'Komórka lokatorska/Piwnica',
    'Miejsce postojowe/Garaż'
  ];

  public otherPurposesPropertyTypes: string[] = [
    'Grunt',
    'Budynek',
    'Budowla'
  ];

  private dictService: TaxExemptionBasisService = inject(TaxExemptionBasisService);

  public taxExemptionBasisList: Signal<TaxExemptionBasisAppEntityDictionaryTaxExemptionBasis[] | undefined> = toSignal(
    this.dictService.getTaxExemptionBasisCollectionTaxExemptionBasisCollection({
      pagination: false,
      orderName: 'asc'
    })
  );

  constructor() {
    super({
      city: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      street: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      houseNumber: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),

      hasLandRegister: new FormControl(true, { nonNullable: true, validators: [Validators.required] }),

      additionalLocationDescription: new FormControl(null, [Validators.maxLength(255)]),

      possessionForm: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      isPartOfNatureReserve: new FormControl(false, { nonNullable: true }),

      isTaxExempt: new FormControl(false, { nonNullable: true }),

      districtNumber: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      parcelNumber: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      areaHa: new FormControl(null, [Validators.required, Validators.maxLength(255)])
    });
    this.hasLandRegisterChange(true);
    this.isTaxExemptChange(false);
  }

  public get areaHa(): FormControl | null {
    return this.get('areaHa') as FormControl | null;
  }

  public get isPartOfNatureReserve(): FormControl | null {
    return this.get('isPartOfNatureReserve') as FormControl | null;
  }

  public get landRegisterNumber(): FormControl | null {
    return this.get('landRegisterNumber') as FormControl | null;
  }

  public get additionalIdentification(): FormControl | null {
    return this.get('additionalIdentification') as FormControl | null;
  }

  public hasLandRegisterChange(v: boolean): void {
    if (v) {
      this.addControl('landRegisterNumber', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
      this.removeControl('additionalIdentification');
      this.additionalIdentificationChange('');
    } else {
      this.removeControl('landRegisterNumber');
      this.addControl('additionalIdentification', new FormControl('Numer zbioru dokumentów', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }));
      this.additionalIdentificationChange('Numer zbioru dokumentów');
    }
  }

  public additionalIdentificationChange(v: AdditionalIdentificationType): void {
    this.removeControl('documentCollectionNumber');
    this.removeControl('notarialDeedNumber');
    this.removeControl('notarialDeedDate');
    this.removeControl('contractNumber');
    this.removeControl('contractDate');
    switch (v) {
      case 'Numer zbioru dokumentów': {
        this.addControl('documentCollectionNumber', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
        break;
      }
      case 'Numer i data aktu notarialnego': {
        this.addControl('notarialDeedNumber', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
        this.addControl('notarialDeedDate', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
        break;
      }
      case 'Numer i data umowy najmu/dzierżawy': {
        this.addControl('contractNumber', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
        this.addControl('contractDate', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
        break;
      }
      default: {
        this.removeControl('documentCollectionNumber');
        this.removeControl('notarialDeedNumber');
        this.removeControl('notarialDeedDate');
        this.removeControl('contractNumber');
        this.removeControl('contractDate');
        break;
      }
    }
  }

  public get documentCollectionNumber(): FormControl | null {
    return this.get('documentCollectionNumber') as FormControl | null;
  }

  public get notarialDeedNumber(): FormControl | null {
    return this.get('notarialDeedNumber') as FormControl | null;
  }

  public get notarialDeedDate(): FormControl | null {
    return this.get('notarialDeedDate') as FormControl | null;
  }

  public get contractNumber(): FormControl | null {
    return this.get('contractNumber') as FormControl | null;
  }

  public get contractDate(): FormControl | null {
    return this.get('contractDate') as FormControl | null;
  }

  public get isTaxExempt(): FormControl | null {
    return this.get('isTaxExempt') as FormControl;
  }

  public isTaxExemptChange(v: boolean): void {
    if (v) {
      this.createTaxExemptionBasisControl();
    } else {
      this.removeControl('taxExemptionBasis');
    }
  }

  public createTaxExemptTypeControl(): void {
    this.addControl('taxExemptType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get taxExemptType(): FormControl | null {
    return this.get('taxExemptType') as FormControl | null;
  }

  public taxExemptTypeChange(v: string): void {
    if (v === 'Podlega zwolnieniu') {
      this.createTaxExemptionBasisControl();
    } else {
      this.removeControl('taxExemptionBasis');
    }
  }

  public get taxExemptionBasis(): FormControl | null {
    return this.get('taxExemptionBasis') as FormControl | null;
  }

  private createTaxExemptionBasisControl(): void {
    this.addControl('taxExemptionBasis', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get groundType(): FormControl | null {
    return this.get('groundType') as FormControl | null;
  }

  public createGroundTypeControl(): void {
    this.addControl('groundType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get usageClass(): FormControl | null {
    return this.get('usageClass') as FormControl | null;
  }

  public createUsageClassControl(): void {
    this.addControl('usageClass', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get propertyType(): FormControl | null {
    return this.get('propertyType') as FormControl | null;
  }

  public createPropertyTypeControl(): void {
    this.addControl(
      'propertyType',
      new FormControl('Dom jednorodzinny', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      })
    );
  }

  public createAreaTypeControl(): void {
    this.addControl('areaType', new FormControl('Łączna powierzchnia', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
    this.areaTypeChange('Łączna powierzchnia');
  }

  public get areaType(): FormControl | null {
    return this.get('areaType') as FormControl | null;
  }

  public areaTypeChange(v: string): void {
    switch (v) {
      case 'Łączna powierzchnia': {
        this.addControl(
          'areaM2',
          new FormControl('0,00', {
            validators: [Validators.required, Validators.maxLength(255)],
            nonNullable: true
          })
        );

        this.removeControl('areaAbove22m');
        this.removeControl('areaBetween14and22m');
        break;
      }
      case 'Powierzchnia z podziałem': {
        this.addControl(
          'areaAbove22m',
          new FormControl('0,00', {
            validators: [Validators.required, Validators.maxLength(255)],
            nonNullable: true
          })
        );
        this.addControl(
          'areaBetween14and22m',
          new FormControl('0,00', {
            validators: [Validators.required, Validators.maxLength(255)],
            nonNullable: true
          })
        );

        this.removeControl('areaM2');
        break;
      }
      default: {
        this.removeControl('areaM2');
        this.removeControl('areaAbove22m');
        this.removeControl('areaBetween14and22m');
      }
    }

  }

  public get areaM2(): FormControl | null {
    return this.get('areaM2') as FormControl | null;
  }

  public get areaAbove22m(): FormControl | null {
    return this.get('areaAbove22m') as FormControl | null;
  }

  public get areaBetween14and22m(): FormControl | null {
    return this.get('areaBetween14and22m') as FormControl | null;
  }

  public override patchFormValues(value: TaxObjectAppEntityTaxObjectWrite): void {
    this.hasLandRegisterChange(!!value?.hasLandRegister);
    this.additionalIdentificationChange((value?.additionalIdentification || '') as AdditionalIdentificationType);
    this.isTaxExemptChange(!!value?.isTaxExempt);
    this.taxExemptTypeChange(value?.taxExemptType || '');
    this.areaTypeChange(value?.areaType || 'Łączna powierzchnia');
    super.patchFormValues({
      ...value,
      areaHa: value?.areaHa || '',
      groundType: value?.groundType || '',
      usageClass: value?.usageClass || '',
      propertyType: value?.propertyType || 'Dom jednorodzinny',
      areaType: value?.areaType || 'Łączna powierzchnia',
      areaM2: value?.areaM2 || '0,00',
      areaAbove22m: value?.areaAbove22m || '0,00',
      areaBetween14and22m: value?.areaBetween14and22m || '0,00'
    });
  }

  public toWriteModel(): TaxObjectAppEntityTaxObjectWrite {
    return {
      ...this.value,
      areaHa: this.areaHa?.value ?  toNumber(this.areaHa.value) : null,
      additionalIdentification: this.additionalIdentification?.value || null,
      documentCollectionNumber: this.documentCollectionNumber?.value || null,
      notarialDeedNumber: this.notarialDeedNumber?.value || null,
      notarialDeedDate: this.notarialDeedDate?.value || null,
      contractNumber: this.contractNumber?.value || null,
      contractDate: this.contractDate?.value || null,
      landRegisterNumber: this.landRegisterNumber?.value || null,
      taxExemptionBasis: this.taxExemptionBasis?.value || null,
      isTaxExempt: this.isTaxExempt?.value || null,
      isPartOfNatureReserve: this.isPartOfNatureReserve?.value || null,
      groundType: this.groundType?.value || null,
      usageClass: this.usageClass?.value || null,
      propertyType: this.propertyType?.value || null,
      areaType: this.areaType?.value || null,
      areaM2: this.areaM2?.value ? toNumber(this.areaM2?.value) : null,
      areaAbove22m: this.areaAbove22m?.value ? toNumber(this.areaAbove22m?.value) : null,
      areaBetween14and22m: this.areaBetween14and22m?.value ? toNumber(this.areaBetween14and22m?.value) : null
    };
  }
}
